import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Environment & Devices — workspace, devices, tools, and inventory.
 *
 * A calm record of the physical things that support the owner's work and life,
 * so attention isn't lost to "where is it / what state is it in". Items carry a
 * category, a lifecycle status, and a location. No surveillance, no clutter.
 */

export type ItemCategory = 'device' | 'tool' | 'space' | 'supply' | 'other';
export const ITEM_CATEGORIES: readonly ItemCategory[] = ['device', 'tool', 'space', 'supply', 'other'] as const;

export type ItemStatus = 'in_use' | 'storage' | 'repair' | 'retired';
export const ITEM_STATUSES: readonly ItemStatus[] = ['in_use', 'storage', 'repair', 'retired'] as const;

export interface InventoryItem extends RecordEnvelope {
  readonly kind: 'inventory_item';
  readonly name: string;
  readonly category: ItemCategory;
  readonly status: ItemStatus;
  readonly location: string;
  readonly note: string;
  readonly linkedIds: readonly EntityId[];
}

export interface InventoryItemInput {
  readonly name: string;
  readonly category?: ItemCategory;
  readonly location?: string;
  readonly note?: string;
  readonly privacyClass?: PrivacyClass;
}

export interface InventoryItemPatch {
  readonly name?: string;
  readonly category?: ItemCategory;
  readonly location?: string;
  readonly note?: string;
}

/** Items needing attention are those in repair. */
export function needsAttention(status: ItemStatus): boolean {
  return status === 'repair';
}

export interface InventorySummary {
  readonly total: number;
  readonly inUse: number;
  readonly needsAttention: number;
  readonly byCategory: Record<ItemCategory, number>;
}

/** Pure inventory roll-up. */
export function summarizeInventory(
  items: readonly Pick<InventoryItem, 'category' | 'status'>[],
): InventorySummary {
  const byCategory: Record<ItemCategory, number> = { device: 0, tool: 0, space: 0, supply: 0, other: 0 };
  let inUse = 0;
  let attention = 0;
  for (const i of items) {
    byCategory[i.category] += 1;
    if (i.status === 'in_use') inUse += 1;
    if (needsAttention(i.status)) attention += 1;
  }
  return { total: items.length, inUse, needsAttention: attention, byCategory };
}
